import { motion } from "framer-motion";
import { Cloud, CreditCard, Globe, BrainCircuit, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { copy } from "@/content";

const icons = [Cloud, CreditCard, Globe, BrainCircuit];
const iconColors = ["text-acid", "text-brand-blue", "text-brand-green", "text-acid"];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Services = () => {
  const { lang } = useLanguage();
  const t = copy[lang].services;

  return (
    <section id="servicos" className="border-b border-white/10" data-testid="services-section">
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
        <motion.p variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={0} className="mb-4 font-tty text-xs uppercase tracking-[0.3em] text-acid">
          {t.kicker}
        </motion.p>
        <div className="mb-20 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <motion.h2 variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={1} className="font-display text-4xl font-medium tracking-tight text-white md:text-6xl" data-testid="services-title">
            {t.title}
          </motion.h2>
          <motion.p variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} custom={2} className="max-w-sm font-tty text-sm leading-relaxed text-neutral-400">
            {t.subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-6" data-testid="services-grid">
          {t.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.a
                key={item.index}
                href="#contacto"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={i + 1}
                className={`group relative flex min-h-[320px] flex-col justify-between bg-ink p-8 transition-colors duration-500 hover:bg-acid md:p-10 ${i % 3 === 0 ? "md:col-span-4" : "md:col-span-2"}`}
                data-testid={`service-card-${item.index}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-tty text-xs tracking-[0.3em] text-neutral-500 transition-colors duration-500 group-hover:text-black/60">
                    /{item.index}
                  </span>
                  <Icon className={`h-7 w-7 transition-colors duration-500 group-hover:text-black ${iconColors[i]}`} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-medium tracking-tight text-white transition-colors duration-500 group-hover:text-black md:text-3xl">
                    {item.title}
                  </h3>
                  <p className="mt-4 max-w-md font-tty text-sm leading-relaxed text-neutral-400 transition-colors duration-500 group-hover:text-black/70">
                    {item.desc}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span key={tag} className="border border-white/15 px-3 py-1 font-tty text-[10px] uppercase tracking-widest text-neutral-400 transition-colors duration-500 group-hover:border-black/30 group-hover:text-black/70">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <ArrowUpRight className="absolute bottom-8 right-8 h-5 w-5 text-neutral-600 opacity-0 transition-all duration-500 group-hover:text-black group-hover:opacity-100" />
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
